import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u5_x4_ps300';
const IMAGE_BASE = '/assets/drawable/';

type TextBlock = { heading: string; body: string };

const textBlocks: TextBlock[] = [
  {
    heading: '',
    body: 'Differenzdruckschalter wie der Thermokon PS300 überwachen, ob ein bestimmter Druckunterschied über- oder unterschritten wird. Sie geben ein EIN/AUS-Signal (Schaltkontakt) und dienen oft zur Strömungs- oder Filterüberwachung. Im Gegensatz zu Sensoren liefern sie keinen genauen Messwert, sondern agieren als Grenzwertmelder.'
  },
  {
    heading: 'Thermokon PS300 im Detail',
    body: `Der PS300 ist ein mechanischer Differenzdruckschalter mit einem einstellbaren Schaltpunkt (z.B. 20-300 Pa, **genauen Bereich und Modellvariante beachten!**). Er verfügt über einen potentialfreien Wechselkontakt (COM/NO/NC) und benötigt meist keine eigene Spannungsversorgung für die Schaltfunktion.

**Typische Anwendung:** Überwachung der Luftströmung in Lüftungskanälen (z.B. für Keilriemenriss) oder als einfacher Filterwächter.`
  },
  {
    heading: 'Einstellung des Schaltpunkts und Anschluss',
    body: `**1. Schaltpunkt justieren:** Der gewünschte Differenzdruck, bei dem der Schalter auslösen soll, wird direkt am PS300 über das Einstellrad eingestellt. Für eine Keilriemenrissüberwachung wird der Schaltpunkt typischerweise etwas unterhalb des normalen Betriebsdrucks des Ventilators festgelegt.

**2. Elektrischer Anschluss:** Die Kontakte (z.B. COM und NO für 'Schließen bei Alarm') werden an einen digitalen Eingang (DI) der DDC angeschlossen. Die DDC erkennt dann den Schaltzustand.

**Hysterese:** Eine eingebaute Schaltdifferenz (Hysterese) verhindert ein ständiges Schalten bei Drücken nahe dem Einstellwert.`
  },
  {
    heading: '',
    body: 'Differenzdruckschalter wie der PS300 sind robuste und kostengünstige Komponenten zur Grenzwertüberwachung. Die sorgfältige Einstellung des Schaltpunkts und der korrekte elektrische Anschluss sind entscheidend für ihre zuverlässige Funktion und somit für die Sicherheit und Effizienz der HLK-Anlage.'
  }
];

const images = [
  { id: 'img_ps300_beispiel', label: 'Thermokon PS300', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Beispiel eines Thermokon PS300 Druckschalters' },
  { id: 'img_ps300_einstellrad', label: 'Einstellrad', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Einstellrad für den Schaltpunkt am PS300' },
  { id: 'img_schaltplan_di', label: 'Schaltplan PS300', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Schaltplan: Anschluss PS300 an digitalen Eingang' }
];

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = React.useMemo(
    () => textBlocks.map((tb, i) => ({ ...tb, body: override?.texts?.[`block_${i}`] || tb.body })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullPlainText = React.useMemo(() => {
    return mergedBlocks.map(b => `${b.heading ? b.heading + '. ' : ''}${b.body}`).join(' ');
  }, [mergedBlocks]);

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(
    UnitPageShell,
    { title: '6.5.4 Differenzdruckschalter (z.B. PS300)', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      // Bilder-Spalte
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img =>
          React.createElement(ZoomableImage, {
            key: img.id,
            src: imgSrc(img.id, img.src),
            alt: img.alt,
            label: img.label,
            onReplace: canUploadImages ? (file: File) => handleImageReplace(img.id, file) : undefined
          })
        )
      ),

      // Text-Spalte
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map((block, i) =>
          React.createElement(EditableTextBlock, {
            key: i,
            pageId: PAGE_ID,
            blockId: `block_${i}`,
            heading: block.heading,
            body: block.body,
            onSave: (newBody: string) => handleTextSave(`block_${i}`, newBody)
          })
        ),

        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, {
            variant: 'outlined',
            onClick: () => navigate('/units/m6_u5_x99_quiz')
          }, 'Zum Quiz')
        )
      )
    )
  );
};

export default Page;


